/**
 * Local publication lifecycle.
 *
 * Capture stays in `localMedia.ts` (real getUserMedia/getDisplayMedia, real
 * client-side blur) and this module only decides what reaches the SFU. Turning
 * a device OFF unpublishes AND stops the capture, so the OS camera light and
 * mic indicator tell the truth — a muted-but-live track would not.
 */
import type { QualityProfile } from '@cyphex/types';
import { Track, type LocalTrackPublication, type Room } from 'livekit-client';
import type { ScreenShareHint } from './contract';
import { AUDIO_SETTINGS } from '@cyphex/types';
import { screenShareCodec, screenShareEncoding, verifyShareCodec } from './livekitCodec';
import type { LocalMediaController } from './localMedia';
import { cameraPublishBitrate, SHARE_AUDIO_BITRATE } from './shareBudget';

export interface LocalPublisher {
  /** Publishes, or hot-swaps an already published track (device/blur change). */
  syncCamera(): Promise<void>;
  syncMic(): Promise<void>;
  unpublishCamera(): Promise<void>;
  unpublishMic(): Promise<void>;
  publishScreen(hint: ScreenShareHint): Promise<void>;
  unpublishScreen(): Promise<void>;
  readonly screenPublished: boolean;
  /** The share's soundtrack reached the SFU. False when the source had none. */
  readonly screenAudioPublished: boolean;
}

const firstVideo = (stream: MediaStream | null): MediaStreamTrack | null =>
  stream?.getVideoTracks()[0] ?? null;

const firstAudio = (stream: MediaStream | null): MediaStreamTrack | null =>
  stream?.getAudioTracks()[0] ?? null;

export function createLocalPublisher(
  room: Room,
  local: LocalMediaController,
  getProfile: () => QualityProfile,
): LocalPublisher {
  let camera: LocalTrackPublication | null = null;
  let mic: LocalTrackPublication | null = null;
  let screen: LocalTrackPublication | null = null;
  let screenAudio: LocalTrackPublication | null = null;

  const sync = async (
    current: LocalTrackPublication | null,
    track: MediaStreamTrack | null,
    source: Track.Source,
  ): Promise<LocalTrackPublication | null> => {
    if (!track) return current;
    const published = current?.track;
    if (published) {
      // Same track already on the wire — republishing would drop a keyframe.
      if (published.mediaStreamTrack === track) return current;
      await published.replaceTrack(track, true);
      return current;
    }
    return room.localParticipant.publishTrack(track, { source });
  };

  const drop = async (current: LocalTrackPublication | null): Promise<null> => {
    if (current?.track) await room.localParticipant.unpublishTrack(current.track, false);
    return null;
  };

  return {
    get screenPublished() {
      return screen !== null;
    },

    get screenAudioPublished() {
      return screenAudio !== null;
    },

    async syncCamera() {
      camera = await sync(camera, firstVideo(local.cameraStream), Track.Source.Camera);
    },

    async syncMic() {
      mic = await sync(mic, firstAudio(local.micStream), Track.Source.Microphone);
    },

    async unpublishCamera() {
      camera = await drop(camera);
    },

    async unpublishMic() {
      mic = await drop(mic);
    },

    async publishScreen(hint) {
      const track = firstVideo(local.screenStream);
      if (!track) return;
      /* The share is budgeted against the camera, and the camera never loses —
         see `shareBudget.shareBitrate` for the arithmetic. Read HERE, at
         publish time, because that is when the encoder ceiling is fixed: a
         camera switched on afterwards cannot lower a live share's ceiling
         without republishing it, and dropping a keyframe on the whole room
         every time a presenter toggles their camera is a worse outcome than a
         brief overshoot that congestion control settles by itself. */
      /*
   * BUDGET FOR THE CAMERA WHETHER OR NOT IT IS ON YET.
   *
   * Reading the camera's CURRENT state was the hole. Start a share with the
   * camera off and it takes the whole ceiling; turn the camera on a moment
   * later and it publishes ~2 350 kbps beside it, while nothing re-derives the
   * share. The total lands ~22% over MAX_PUBLISH_BITRATE and STAYS there.
   *
   * Congestion control does not rescue it: that ceiling exists to protect the
   * SFU's per-participant egress budget, which GCC knows nothing about — it
   * only watches the presenter's own uplink. A reconnect makes it durable,
   * because `republishTracks` reuses the stored publish options, which still
   * carry the un-clamped rate.
   *
   * Reserving the camera's slice up front costs only the `motion` preset, which
   * clamps from 2 500 to the remaining headroom when it would otherwise breach.
   * The default `detail` path sits well under it and is byte-identical.
   */
      const cameraBitrate = cameraPublishBitrate();
      /* The soundtrack is charged to the SHARE's headroom, not the camera's,
         and only when there is one — see `SHARE_AUDIO_BITRATE`. With the default
         `detail` hint this changes nothing at all: standard's 1 500 kbps floor
         still sits 70 kbps inside the headroom that remains. It shaves the
         `motion` premium by ~3% in the one case that would otherwise breach
         MAX_PUBLISH_BITRATE — a camera, a 30 fps share AND its sound. */
      const audioTrack = firstAudio(local.screenStream);
      const shareAudioBitrate = audioTrack === null ? 0 : SHARE_AUDIO_BITRATE;
      const requestedCodec = screenShareCodec();
      screen = await room.localParticipant.publishTrack(track, {
        source: Track.Source.ScreenShare,
        /* `screenShareEncoding`, NOT `videoEncoding`: for a ScreenShare source
           livekit-client's `computeVideoEncodings` reads the former and throws
           the latter away. The picker's hint therefore never reached the
           encoder — every share ran on the room-wide `'detail'` default baked
           into `publishDefaults`, and the `motion` branch of
           `screenShareEncoding` was dead code. */
        screenShareEncoding: screenShareEncoding(
          getProfile(), hint, cameraBitrate, shareAudioBitrate,
        ),
        /* Keeps this publish off the SVC branch that would rewrite our
           `contentHint` to `'motion'` — see `screenShareCodec`. A non-SVC codec
           also makes the inherited `scalabilityMode` inert: it is only read
           inside `if (scalabilityMode && isSVCCodec(videoCodec))`.

           `backupCodec: false` is a guard, not a saving. Today the inherited
           default is VP8 and `screenShareCodec()` also resolves to VP8, so
           livekit-client already skips backup publishing (`backupCodec.codec
           === videoCodec`). Reorder `CODEC_PREFERENCE` so the share lands on
           H.264 and that guard stops firing, and the presenter would publish
           the share TWICE — and not cheaply: `computeTrackBackupEncodings`
           assigns the 360p backup encoding to `videoEncoding`, which the
           ScreenShare branch of `computeVideoEncodings` then discards in favour
           of `screenShareEncoding`. The second publication would run at the
           full share bitrate, not 360p, putting the presenter near
           MEDIA_LIMITS.MAX_PUBLISH_BITRATE. */
        videoCodec: requestedCodec,
        backupCodec: false,
        simulcast: false,
        /* Correct for screen content, and the reason the capture must be
           bounded in `localMedia`: the encoder may not shed pixels under
           pressure, so an oversized frame is paid for with the quantizer. */
        degradationPreference: 'maintain-resolution',
      });
      /* `publication.options` is the options object livekit-client MUTATED on
         its way through `publishTrack`, so it holds the codec that was actually
         negotiated rather than the one we asked for. */
      verifyShareCodec(screen.options?.videoCodec, requestedCodec);

      /*
       * THE SOUNDTRACK, published AFTER the picture and never instead of it.
       *
       * A share that loses its audio is a share; a share that loses its video
       * because the audio publish failed is a broken feature. So this is a
       * separate, catchable step — and the video above has already landed by
       * the time it runs.
       *
       * `dtx: false` is the one setting that matters here. Discontinuous
       * transmission is a SPEECH optimisation: it stops sending during silence
       * and the decoder fills the gap with comfort noise. On a soundtrack that
       * clips the leading edge of every effect and puts a hiss under every
       * quiet passage. `AUDIO_SETTINGS.red` stays on — redundancy is about
       * packet loss, and loss hurts music more than speech, not less.
       */
      if (audioTrack === null) return;
      screenAudio = await room.localParticipant
        .publishTrack(audioTrack, {
          source: Track.Source.ScreenShareAudio,
          audioPreset: { maxBitrate: SHARE_AUDIO_BITRATE },
          dtx: false,
          red: AUDIO_SETTINGS.red,
          forceStereo: false,
        })
        .catch(() => null);
    },

    async unpublishScreen() {
      /* Audio first: the roster's `sharing` flag follows the VIDEO track, and
         unpublishing that first would leave a beat in which the share is over
         and its sound is still on the wire. */
      screenAudio = await drop(screenAudio);
      screen = await drop(screen);
    },
  };
}
